import type { PaymentSystem, SolidgatePayPalOrder } from '@web-solutions/react-billing';
import type { Currency, Product } from '@web-solutions/react-billing/constants';

import type { OrderDetails, PaddleConfig, Purchase } from '@web-solutions/core/interfaces/billing';
import type { UpsaleBasketItem } from '@web-solutions/core/interfaces/upsaleBasket';

import * as TYPES from './types';
import { type ProductDetails } from './selectors';
import { PlanTypes } from 'core/constants/remote-config';

export interface OneTimePurchase {
  product_code: string;
  payment_service: PaymentSystem;
  currency: Currency;
  id?: number;
  order_id?: string;
}

export type PaymentMethod = 'card' | 'applepay' | 'googlepay' | 'paypal' | 'mercadopago';

export interface FollowersGrowing {
  selectedPlan: PlanTypes;
  maxFollowers: number;
}

export interface BillingState {
  isInitBilling: boolean;
  purchased: boolean;
  loading: boolean;
  pending: boolean;
  isSubmitByCard: boolean;
  orderPending: boolean;
  postcode: string;
  paymentSystem: PaymentSystem | null;
  purchase: Purchase | null;
  orderDetails: OrderDetails | null;
  ordersPayPal: Record<string, SolidgatePayPalOrder>;
  oneTimeProducts: Record<string, Product>;
  paddleConfig: PaddleConfig | null;
  products: ProductDetails[];
  discountEndDate: string | null;
  noFundsOfferEndDate: string | null;
  specialOfferEndDate: string | null;
  paymentMethod: PaymentMethod | null;
  specialOfferProducts: ProductDetails[];
  noFundsOfferProducts: ProductDetails[];
  promocodeOfferProducts: ProductDetails[];
  trialPrice: number | null;
  promocodeActivated: boolean;
  injectedPaymentMethod: string | null;
  oneTimePurchases: OneTimePurchase[];
  trialsLeftCounter: number;
  countryCode: string | null;
  upsalesBasket: UpsaleBasketItem[];
  oneTimePurchaseId: number | null;
  isOpenErrorPopup: boolean;
  isPaymentOpen: boolean;
  wasOpenPaymentPopup: boolean;
  accessTokenUserAPI: string | null;
  activeProduct: ProductDetails | null;
  showDownSaleModalOnce: boolean;
  selectedSpecialOfferProduct: boolean;
  countPPClosed: number;
  isDisabledButton: boolean;
  followersGrowing: FollowersGrowing;
  subscriptionConsentsCheckedList: boolean[] | null;
}

export const initialState: BillingState = {
  isInitBilling: false,
  paymentSystem: null,
  purchase: null,
  purchased: false,
  loading: false,
  pending: false,
  isSubmitByCard: false,
  orderDetails: null,
  ordersPayPal: {},
  orderPending: false,
  paddleConfig: null,
  paymentMethod: null,
  products: [],
  upsalesBasket: [],
  discountEndDate: null,
  noFundsOfferEndDate: null,
  oneTimeProducts: {},
  specialOfferEndDate: null,
  specialOfferProducts: [],
  noFundsOfferProducts: [],
  promocodeOfferProducts: [],
  promocodeActivated: false,
  trialPrice: null,
  postcode: '',
  injectedPaymentMethod: null,
  oneTimePurchases: [],
  trialsLeftCounter: 10,
  countryCode: null,
  oneTimePurchaseId: null,
  isOpenErrorPopup: false,
  isPaymentOpen: false,
  wasOpenPaymentPopup: false,
  accessTokenUserAPI: null,
  isDisabledButton: false,
  activeProduct: null,
  showDownSaleModalOnce: false,
  selectedSpecialOfferProduct: false,
  countPPClosed: 0,
  followersGrowing: {
    selectedPlan: 'basic',
    maxFollowers: 1000,
  },
  subscriptionConsentsCheckedList: null,
};

type BillingAction = TYPES.BillingActionTypes | TYPES.SetSubscriptionConsentsCheckedList;

const reducer = (state = initialState, action: BillingAction) => {
  switch (action.type) {
    case TYPES.SET_IS_INIT_BILLING:
      return {
        ...state,
        isInitBilling: action.payload,
      };
    case TYPES.SET_PAYMENT_METHOD:
      return {
        ...state,
        paymentMethod: action.payload,
      };
    case TYPES.SET_PAYMENT_SYSTEM:
      return {
        ...state,
        paymentSystem: action.payload.paymentSystem,
      };
    case TYPES.SET_PROMOCODE_ACTIVATED:
      return {
        ...state,
        promocodeActivated: action.payload,
      };

    case TYPES.SET_ONE_TIME_PURCHASES:
      return {
        ...state,
        oneTimePurchases: action.payload,
      };
    case TYPES.SET_PURCHASE:
      return {
        ...state,
        purchase: action.payload.purchase,
        purchased: !!action.payload.purchase,
      };

    case TYPES.SET_LOADING:
      return {
        ...state,
        loading: action.payload,
      };

    case TYPES.SET_PENDING:
      return {
        ...state,
        pending: action.payload,
      };

    case TYPES.SET_IS_SUBMIT_BY_CARD:
      return {
        ...state,
        isSubmitByCard: action.payload,
      };

    case TYPES.SET_ORDER_DETAILS:
      return {
        ...state,
        orderDetails: action.payload,
      };

    case TYPES.SET_ORDERS_PAYPAL:
      return {
        ...state,
        ordersPayPal: {
          ...state.ordersPayPal,
          ...action.payload,
        },
      };

    case TYPES.SET_ORDER_PENDING:
      return {
        ...state,
        orderPending: action.payload,
      };

    case TYPES.SET_PADDLE_CONFIG:
      return {
        ...state,
        paddleConfig: action.payload,
      };

    case TYPES.SET_PRODUCTS:
      return {
        ...state,
        products: action.payload,
      };

    case TYPES.SET_DISCOUNT_END_DATE:
      return {
        ...state,
        discountEndDate: action.payload,
      };

    case TYPES.SET_NO_FUNDS_END_DATE:
      return {
        ...state,
        noFundsOfferEndDate: action.payload,
      };

    case TYPES.SET_SPECIAL_OFFER_END_DATE:
      return {
        ...state,
        specialOfferEndDate: action.payload,
      };

    case TYPES.SET_SPECIAL_OFFER_PRODUCTS:
      return {
        ...state,
        specialOfferProducts: action.payload,
      };

    case TYPES.SET_NO_FUNDS_OFFER_PRODUCTS:
      return {
        ...state,
        noFundsOfferProducts: action.payload,
      };

    case TYPES.SET_ONE_TIME_PRODUCTS:
      return {
        ...state,
        oneTimeProducts:
          action.payload && action.payload?.length
            ? action.payload.reduce(
                (acc, product) => {
                  acc[product.id] = product;

                  return acc;
                },
                {} as Record<string, Product>
              )
            : state.oneTimeProducts,
      };

    case TYPES.SET_PROMOCODE_OFFER_PRODUCTS:
      return {
        ...state,
        promocodeOfferProducts: action.payload,
      };
    case TYPES.SET_TRIAL_PRICE:
      return {
        ...state,
        trialPrice: action.payload,
      };

    case TYPES.SET_POSTCODE:
      return {
        ...state,
        postcode: action.payload,
      };

    case TYPES.RESET:
      return {
        ...initialState,
      };

    case TYPES.SET_INJECTED_PAYMENT_METHOD:
      return {
        ...state,
        injectedPaymentMethod: action.payload,
      };

    case TYPES.SET_TRIALS_LEFT_COUNTER:
      return {
        ...state,
        trialsLeftCounter: state.trialsLeftCounter - 1,
      };

    case TYPES.SET_COUNTRY_CODE:
      return {
        ...state,
        countryCode: action.payload,
      };

    case TYPES.SET_UPSALE_TO_BASKET: {
      return {
        ...state,
        upsalesBasket: [...state.upsalesBasket, action.payload].sort(
          (a, b) => Number(b.checked) - Number(a.checked)
        ),
      };
    }

    case TYPES.UPDATE_UPSALE_IN_BASKET: {
      return {
        ...state,
        upsalesBasket: state.upsalesBasket.map((upsale) =>
          upsale.id === action.payload.id ? { ...upsale, ...action.payload } : upsale
        ),
      };
    }
    case TYPES.SET_ONE_TIME_PURCHASE_ID: {
      return {
        ...state,
        oneTimePurchaseId: action.payload,
      };
    }

    case TYPES.CHANGE_PURCHASE_TOKEN_ID: {
      return {
        ...state,
        purchase: {
          ...state.purchase,
          token_id: action.payload,
        },
      };
    }

    case TYPES.SET_IS_OPEN_ERROR_POPUP: {
      return {
        ...state,
        isOpenErrorPopup: action.payload,
      };
    }

    case TYPES.SET_IS_PAYMENT_OPEN: {
      return {
        ...state,
        isPaymentOpen: action.payload,
      };
    }

    case TYPES.SET_ACCESS_TOKEN_USER_API: {
      return {
        ...state,
        accessTokenUserAPI: action.payload,
      };
    }

    case TYPES.SET_CARD_READY: {
      return {
        ...state,
        isFormReady: action.payload,
      };
    }

    case TYPES.SET_WAS_OPEN_PAYMENT_POPUP: {
      return {
        ...state,
        wasOpenPaymentPopup: state.wasOpenPaymentPopup || action.payload,
      };
    }

    case TYPES.SET_ACTIVE_PRODUCT: {
      return {
        ...state,
        activeProduct: action.payload,
      };
    }

    case TYPES.SET_IS_DISABLED_BUTTON: {
      return {
        ...state,
        isDisabledButton: action.payload,
      };
    }

    case TYPES.SET_DOWN_SALE_MODAL_ONCE: {
      return {
        ...state,
        showDownSaleModalOnce: action.payload,
      };
    }

    case TYPES.SET_SELECTED_SPECIAL_OFFER_PRODUCT: {
      return {
        ...state,
        selectedSpecialOfferProduct: action.payload,
      };
    }

    case TYPES.SET_COUNT_PP_CLOSE: {
      return {
        ...state,
        countPPClosed: action.payload,
      };
    }

    case TYPES.SET_FOLLOWERS_GROWING: {
      return {
        ...state,
        followersGrowing: action.payload,
      };
    }

    case TYPES.SET_SUBSCRIPTION_CONSENTS_CHECKED_LIST: {
      return {
        ...state,
        subscriptionConsentsCheckedList: action.payload,
      };
    }

    default:
      return state;
  }
};

reducer.whitelist = [
  'subscriptionDetails',
  'purchase',
  'purchased',
  'discountEndDate',
  'paymentMethod',
  'specialOfferEndDate',
  'noFundsOfferEndDate',
  'trialPrice',
  'paddleConfig',
  'postcode',
  'wasOpenPaymentPopup',
];

reducer.blacklist = ['isFormReady', 'isInitBilling'];

export default reducer;
